








































































































































































































































import Vue from "vue";
import CheckBox from "@/components/CheckBox.vue";
import IconButton from "@/components/IconButton.vue";
import LookupComboBox from "@/components/LookupComboBox.vue";
import MoneyDisplay from "@/components/MoneyDisplay.vue";
import eventBus from "@/services/eventBus";
import CurrentAccountPreAccountingApi from "../services/CurrentAccountPreAccountingApi";
import {
  CurrentAccountAccItem,
  CurrentAccountAccNo,
  CurrentAccountActionItem,
  CurrentAccountDimCode,
} from "../models/CurrentAccountPreAccounting";
import { t } from "@/plugins/i18n";

/** Az éppen inline szerkesztett kontír sor állapota (id = 0: nincs szerkesztés) */
interface AccEditState {
  id: number;
  accCode: string;
  dimcode1: string;
  dimcode2: string;
  dimcode3: string;
  accNos: CurrentAccountAccNo[];
  dimCodes: CurrentAccountDimCode[];
}

const emptyEdit = (): AccEditState => ({
  id: 0,
  accCode: "",
  dimcode1: "",
  dimcode2: "",
  dimcode3: "",
  accNos: [],
  dimCodes: [],
});

/** A dimenziókód-választék típusa mezőnként (K2D f_dimcode_popup_os dim_no): 1. dim → 2-es típus … */
const DIM_TYPE_BY_FIELD: Record<string, number> = { dimcode1: 2, dimcode2: 3, dimcode3: 4 };

interface Data {
  originalFlags: Record<number, boolean>;
  isSavingFlags: boolean;
  edit: AccEditState;
  isLoadingEdit: boolean;
  isSavingAcc: boolean;
  // nem reaktív segédek a napló-detail colspan igazításához
  detailObserver: MutationObserver | null;
  colSpanFrame: number;
}

interface Methods {
  captureFlags(): void;
  toggleIsUp(item: CurrentAccountActionItem): void;
  resetFlags(): void;
  saveFlags(): Promise<void>;
  toggleAccItems(item: CurrentAccountActionItem): void;
  fixLogColSpan(): void;
  toggleAccLog(accItem: CurrentAccountAccItem): Promise<void>;
  startAccEdit(accItem: CurrentAccountAccItem): Promise<void>;
  cancelAccEdit(): void;
  saveAccEdit(accItem: CurrentAccountAccItem): Promise<void>;
  dimItems(field: string): CurrentAccountDimCode[];
  onEditAccNo(value: any): void;
  onEditDimCode(field: string, value: any): void;
}

interface Computed {
  hasFlagChange: boolean;
  itemColumns: any[];
  accItemColumns: any[];
}

interface Props {
  actionId: number;
  items: CurrentAccountActionItem[];
  isUpChangeable: boolean;
  canModify: boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    CheckBox,
    IconButton,
    LookupComboBox,
    MoneyDisplay,
  },

  props: {
    actionId: { type: Number, default: 0 },
    items: { type: Array as () => CurrentAccountActionItem[], default: () => [] },
    // pssyssysfile1.is_up_changeable — a Felad oszlop csak ekkor látszik (K2D ue_retrievemaster)
    isUpChangeable: { type: Boolean, default: false },
    // K2D ue_modify kapuja: a művelet kontír státusza nem 3/4/6
    canModify: { type: Boolean, default: false },
  },

  data() {
    return {
      originalFlags: {},
      isSavingFlags: false,
      edit: emptyEdit(),
      isLoadingEdit: false,
      isSavingAcc: false,
      detailObserver: null,
      colSpanFrame: 0,
    };
  },

  created() {
    this.captureFlags();
  },

  // A kontír sor grid napló-detail sorának colspan-ját a Kendo lenyitáskor (újra)számolja,
  // ezért a DOM változásait figyelve igazítjuk (az ApiSearchKendoGrid.fixDetailColSpan mintája)
  mounted() {
    this.fixLogColSpan();
    const observer = new MutationObserver(() => {
      if (this.colSpanFrame) return;
      this.colSpanFrame = requestAnimationFrame(() => {
        this.colSpanFrame = 0;
        this.fixLogColSpan();
      });
    });
    observer.observe(this.$el, { childList: true, subtree: true });
    this.detailObserver = observer;
  },

  beforeDestroy() {
    if (this.detailObserver) this.detailObserver.disconnect();
    if (this.colSpanFrame) cancelAnimationFrame(this.colSpanFrame);
  },

  watch: {
    items() {
      this.captureFlags();
    },
  },

  methods: {
    // A betöltött állapot elmentése, hogy a Mentés csak tényleges változásra legyen aktív
    captureFlags() {
      const flags: Record<number, boolean> = {};
      (this.$props.items ?? []).forEach((item: CurrentAccountActionItem) => {
        flags[item.id] = item.isUp;
      });
      this.originalFlags = flags;
    },

    toggleIsUp(item: CurrentAccountActionItem) {
      if (!this.$props.isUpChangeable || item.accItems.length === 0) return;
      item.isUp = !item.isUp;
    },

    // K2D pb_cancel: a sor újratöltése — itt a betöltött jelölők visszaállítása
    resetFlags() {
      (this.$props.items ?? []).forEach((item: CurrentAccountActionItem) => {
        const original = this.originalFlags[item.id];
        if (original !== undefined) item.isUp = original;
      });
    },

    // K2D pb_ok: csak a megváltozott tételek mennek át (a szerver a kontírsorokra írja)
    async saveFlags() {
      const changed = (this.$props.items ?? [])
        .filter((item: CurrentAccountActionItem) => this.originalFlags[item.id] !== item.isUp)
        .map((item: CurrentAccountActionItem) => ({ itemId: item.id, isUp: item.isUp }));

      if (changed.length === 0) return;

      this.isSavingFlags = true;
      try {
        const result = await CurrentAccountPreAccountingApi.saveUploadFlags(
          this.$props.actionId,
          changed
        );
        this.captureFlags();
        eventBus.$emit(
          "success",
          t("CurrentAccountPreAccounting_AFeladJelolokMentveKontirSor", [result.updatedItems])
        );
        this.$emit("flags-saved");
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette; a szerkesztett
        // állapot marad, hogy a felhasználó újrapróbálhassa
      } finally {
        this.isSavingFlags = false;
      }
    },

    // K2D dw_1::rowfocuschanged → dw_action_acc.retrieve: a tétel kontír sorai lenyílnak
    toggleAccItems(item: CurrentAccountActionItem) {
      if (item.accItems.length === 0) return;
      item.expanded = !item.expanded;
    },

    // A Kendo a részletcella colSpan-ját oszlopszám-1-re veszi (hierarchia-oszlopot feltételez,
    // nálunk az nincs), ezért a napló-sor az utolsó oszlopig nem érne el
    fixLogColSpan() {
      const count = this.accItemColumns.length;
      this.$el.querySelectorAll<HTMLTableCellElement>(".detail-acc-grid td.k-detail-cell").forEach((td) => {
        if (td.colSpan !== count) td.colSpan = count;
      });
    },

    // A sor módosítási naplója: első megnyitáskor töltődik, mentés után újratölt (a sor alatti
    // detail-sorban látszik)
    async toggleAccLog(accItem: CurrentAccountAccItem) {
      if (accItem.logOpen) {
        Vue.set(accItem, "logOpen", false);
        return;
      }
      Vue.set(accItem, "logOpen", true);
      if (accItem.log !== undefined) return;
      Vue.set(accItem, "logLoading", true);
      try {
        Vue.set(
          accItem,
          "log",
          await CurrentAccountPreAccountingApi.getAccItemLog(this.$props.actionId, accItem.id)
        );
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette
        Vue.set(accItem, "logOpen", false);
      } finally {
        Vue.set(accItem, "logLoading", false);
      }
    },

    // Inline szerkesztés indítása: a szerver adja a sor aktuális értékeit, a szerkeszthetőséget
    // (időközben 3/4/6-ra váltott művelet már nem módosítható) és a választékokat
    async startAccEdit(accItem: CurrentAccountAccItem) {
      if (!this.$props.canModify || this.edit.id !== 0 || this.isLoadingEdit) return;
      this.isLoadingEdit = true;
      try {
        const data = await CurrentAccountPreAccountingApi.getAccItemEditData(accItem.id);
        if (!data.editable) {
          eventBus.$emit("warn", t("CurrentAccountPreAccounting_MarNemLehetAKontirozasonValtoztatni_2"));
          return;
        }
        this.edit = {
          id: accItem.id,
          accCode: data.accCode ?? "",
          dimcode1: data.dimcode1 ?? "",
          dimcode2: data.dimcode2 ?? "",
          dimcode3: data.dimcode3 ?? "",
          accNos: data.accNos ?? [],
          dimCodes: data.dimCodes ?? [],
        };
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette (pl. időközben
        // törlődött a kontír sor)
      } finally {
        this.isLoadingEdit = false;
      }
    },

    cancelAccEdit() {
      this.edit = emptyEdit();
    },

    // K2D w_popup_act_cont_mod pb_ok: a szerver naplóz, újraellenőriz (5 vagy 8) és a
    // művelet státuszát is átírja — a sor mezői itt helyben frissülnek, a lista nem töltődik újra
    async saveAccEdit(accItem: CurrentAccountAccItem) {
      const e = this.edit;
      if (e.id !== accItem.id) return;
      const changed =
        e.accCode !== (accItem.accCode ?? "") ||
        e.dimcode1 !== (accItem.dimcode1 ?? "") ||
        e.dimcode2 !== (accItem.dimcode2 ?? "") ||
        e.dimcode3 !== (accItem.dimcode3 ?? "");
      if (!changed) {
        this.cancelAccEdit();
        return;
      }

      this.isSavingAcc = true;
      try {
        const result = await CurrentAccountPreAccountingApi.saveAccItem(accItem.id, {
          accCode: e.accCode,
          dimcode1: e.dimcode1,
          dimcode2: e.dimcode2,
          dimcode3: e.dimcode3,
        });
        accItem.accCode = e.accCode;
        accItem.dimcode1 = e.dimcode1;
        accItem.dimcode2 = e.dimcode2;
        accItem.dimcode3 = e.dimcode3;
        // A mentés új napló-bejegyzést írt: a következő megnyitáskor újratöltődik
        Vue.set(accItem, "log", undefined);
        if (accItem.logOpen) Vue.set(accItem, "logOpen", false);

        if (result.accStatus === 8) {
          const lines = [t("CurrentAccountPreAccounting_AKontirozasMentveDeAzEllenorzes")];
          result.errors.forEach((err) => lines.push(`${err.errTypeName}: ${err.note}`));
          eventBus.$emit("error", lines.join("\n"));
        } else {
          eventBus.$emit("success", t("CurrentAccountPreAccounting_AKontirozasModositva"));
        }

        this.cancelAccEdit();
        this.$emit("acc-item-saved", result);
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette; a sor szerkesztésben
        // marad, hogy a felhasználó javíthassa
      } finally {
        this.isSavingAcc = false;
      }
    },

    // A dimenziókód-választék típusonként (K2D f_dimcode_popup_os dim_no paramétere)
    dimItems(field: string): CurrentAccountDimCode[] {
      const type = DIM_TYPE_BY_FIELD[field];
      return this.edit.dimCodes.filter((x) => x.type === type);
    },

    onEditAccNo(value: any) {
      this.edit.accCode = value?.accNo ?? value ?? "";
    },

    onEditDimCode(field: string, value: any) {
      (this.edit as any)[field] = value?.dimcode ?? value ?? "";
    },
  },

  computed: {
    hasFlagChange(): boolean {
      return (this.$props.items ?? []).some(
        (item: CurrentAccountActionItem) =>
          this.originalFlags[item.id] !== undefined && this.originalFlags[item.id] !== item.isUp
      );
    },

    // A tétel-grid oszlopai (K2D d_g_action_item_new)
    itemColumns(): any[] {
      const columns: any[] = [
        { title: " ", cell: "itemCommandTemplate", width: "60px" },
        { field: "id", title: t("Tetel"), width: "90px", className: "right_text" },
        { field: "partnerName", title: t("Partner") },
        { field: "prefix", title: t("Prefix"), width: "70px" },
        { field: "invoice", title: t("Szamla"), cell: "invoiceTemplate", width: "90px" },
        { field: "actionTypeName", title: t("TetelTipusa"), width: "180px" },
        { field: "invoiceTypeName", title: t("SzamlaTipusa"), width: "180px" },
        { field: "prevStatName", title: t("CurrentAccountPreAccounting_ElozoStatusz"), width: "130px" },
        { field: "folwStatName", title: t("CurrentAccountPreAccounting_UjStatusz"), width: "130px" },
        { field: "invAmount", title: t("CurrentAccountPreAccounting_SzamlaOsszeg"), cell: "invAmountTemplate", width: "130px" },
        { field: "paymAmount", title: t("CurrentAccountPreAccounting_Fizetett"), cell: "paymAmountTemplate", width: "130px" },
        { field: "currency", title: t("Deviza"), width: "70px" },
        { field: "paymId", title: t("CurrentAccountPreAccounting_FizTetel"), cell: "paymIdTemplate", width: "90px" },
        { field: "paymTableName", title: t("CurrentAccountPreAccounting_FizTetelTabla"), width: "150px" },
        { field: "pDate", title: t("CurrentAccountPreAccounting_FizDatum"), width: "110px", className: "right_text" },
        { field: "actionDate", title: t("Datum"), width: "110px", className: "right_text" },
        { field: "userName", title: t("Felhasznalo"), width: "150px" },
      ];
      // A Felad jelölő a kontír-ikon után, második oszlopként — a sor elején látszik
      if (this.$props.isUpChangeable) {
        columns.splice(1, 0, { field: "isUp", title: t("Felad"), cell: "isUpTemplate", width: "70px" });
      }
      // A detail-sor colspan-ja az oszlopszámból jön, de a Kendo a hierarchia-cellával
      // eltolja — e nélkül a lenyitott sor az utolsó előtti oszlopig érne csak
      columns.push({ width: 0 });
      columns.push({ width: 0 });
      return columns;
    },

    // A kontír-sor grid oszlopai (K2D d_g_action_acc)
    accItemColumns: () => [
      { title: " ", cell: "accCommandTemplate", width: "110px" },
      { field: "accCode", title: t("FokonyviSzam"), cell: "accCodeTemplate", width: "170px" },
      { field: "accSign", title: "T/K", cell: "accSignTemplate", width: "90px" },
      { field: "total", title: t("Osszeg"), cell: "totalTemplate", width: "130px" },
      { field: "currency", title: t("Deviza"), width: "70px" },
      { field: "currTotal", title: t("DevizaOsszeg"), cell: "currTotalTemplate", width: "130px" },
      { field: "exchangeRate", title: t("Arfolyam"), width: "90px", className: "right_text" },
      { field: "accDate", title: t("KonyvelesDatuma"), width: "110px", className: "right_text" },
      { field: "detailedTypeName", title: t("TetelTipus"), width: "160px" },
      { field: "dimcode1", title: t("CurrentAccountPreAccounting_Dim1"), cell: "dimTemplate", width: "150px" },
      { field: "dimcode2", title: t("CurrentAccountPreAccounting_Dim2"), cell: "dimTemplate", width: "150px" },
      { field: "dimcode3", title: t("CurrentAccountPreAccounting_Dim3"), cell: "dimTemplate", width: "150px" },
      { field: "period", title: t("Periodus"), width: "80px", className: "right_text" },
      { field: "note", title: t("Megjegyzes"), cell: "accNoteTemplate" },
      { field: "invCode", title: t("Prefix"), width: "70px" },
      { field: "invNo", title: t("Szamla"), width: "90px", className: "right_text" },
      { field: "payment", title: t("CurrentAccountPreAccounting_FizTetel"), width: "90px", className: "right_text" },
      { field: "bookinDate", title: t("CurrentAccountPreAccounting_FeladasDatuma"), width: "120px", className: "right_text" },
      { field: "isUp", title: t("Felad"), cell: "accIsUpTemplate", width: "70px" },
    ],
  },
});
